// src/operator/panels/policy-editor.tsx
//
// Éditeur de stratégies host pour un scope (tenant | client | site | host).
//
// Affiche les ~50 champs du contrat HostPolicy.cs groupés par thème, avec :
//   - toggles (bool), selects (enum), inputs (number/string)
//   - sélecteur de preset (apply merge/replace avec confirmation + diff)
//   - bouton "Aperçu résolu" (ouvre ResolvedPolicyDrawer, host seulement)
//   - bouton "Sauver" (disabled si pas de diff)
//   - JSON brut repliable (champs hors schéma / power-user)
//
// Le `onSave(policy)` reçoit la policy finale normalisée. L'appelant est
// responsable du PATCH vers la bonne route (PATCH /host/:id, /api/clients/:id,
// /api/sites/:siteId, /api/super-admin/tenants/:id).

import { Show, For, createMemo, createSignal, createEffect, type Component, type JSX } from "solid-js";
import {
  POLICY_FIELDS,
  POLICY_GROUPS,
  diffPolicies,
  applyPreset,
  listUnknownKeys,
  type PolicyObject,
  type PolicyFieldDef,
  type PolicyGroup,
} from "@/shared/host-policy-schema";
import { useConfirm } from "@/shared/confirm-dialog";
import ResolvedPolicyDrawer from "./resolved-policy-drawer";
import "./policy-editor.css";

export type PolicyScopeType = "tenant" | "client" | "site" | "host";

export interface Preset {
  id: string;
  name: string;
  description?: string | null;
  policy: PolicyObject;
  global: boolean;
}

interface Props {
  scopeType: PolicyScopeType;
  scopeId: string;
  scopeLabel?: string;
  initialPolicy: PolicyObject;
  /** Presets disponibles (fetch côté appelant). */
  presets?: Preset[];
  /** Pour scope=host : ouvrir le drawer "aperçu résolu". */
  hostId?: string;
  onSave: (policy: PolicyObject) => Promise<void>;
  onCancel?: () => void;
}

const PolicyEditor: Component<Props> = (props) => {
  const confirmDialog = useConfirm();
  const [draft, setDraft] = createSignal<PolicyObject>({ ...props.initialPolicy });
  const [showJson, setShowJson] = createSignal(false);
  const [jsonText, setJsonText] = createSignal("");
  const [jsonError, setJsonError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = createSignal<Set<PolicyGroup>>(new Set());
  const [showSensitive, setShowSensitive] = createSignal(false);
  const [drawerOpen, setDrawerOpen] = createSignal(false);
  const [presetMode, setPresetMode] = createSignal<"merge" | "replace">("merge");

  // Re-init quand l'appelant change de scope (ex : sélection d'un autre client).
  createEffect(() => {
    void props.scopeId;
    setDraft({ ...props.initialPolicy });
    setErr(null);
    setShowJson(false);
    setJsonError(null);
  });

  const diff = createMemo(() => diffPolicies(props.initialPolicy, draft()));
  const isDirty = createMemo(() => diff().length > 0);
  const dirtyKeys = createMemo(() => new Set(diff().map((d) => d.key)));

  const unknownKeys = createMemo(() => listUnknownKeys(draft()));

  // Champs effectivement présents dans le draft, groupés par groupe.
  const groupedFields = createMemo(() => {
    const out: { group: (typeof POLICY_GROUPS)[number]; fields: PolicyFieldDef[] }[] = [];
    for (const g of POLICY_GROUPS) {
      const fields = POLICY_FIELDS.filter((f) => f.group === g.id);
      // Toujours afficher tous les champs (même ceux à leur valeur par défaut),
      // pour permettre l'édition complète. Le backend remplace la policy stockée
      // par le payload reçu, donc save() envoie le draft complet.
      if (fields.length > 0) out.push({ group: g, fields });
    }
    return out;
  });

  function toggleGroup(g: PolicyGroup) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  function setField(key: string, value: PolicyObject[string]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function clearField(key: string) {
    setDraft((prev) => {
      const next: PolicyObject = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateNumber(field: PolicyFieldDef, v: string): number | null {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    if (field.min !== undefined && n < field.min) return null;
    if (field.max !== undefined && n > field.max) return null;
    return n;
  }

  async function save() {
    if (!isDirty() || busy()) return;
    setBusy(true);
    setErr(null);
    try {
      await props.onSave({ ...draft() });
      // L'appelant re-fetch normalement ; on re-bascule le draft sur initial
      // pour éviter un dirty fantôme si onSave ne propage pas la nouvelle policy.
      // (Les consoles appelantes refont un query qui reset initialPolicy.)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function applyPresetById(presetId: string) {
    const preset = (props.presets || []).find((p) => p.id === presetId);
    if (!preset) return;
    const preview = applyPreset(draft(), preset.policy, presetMode());
    const diff = diffPolicies(draft(), preview);
    const count = diff.length;
    const verb = presetMode() === "replace" ? "remplacer" : "fusionner";
    const ok = await confirmDialog.confirm({
      title: `Appliquer le preset « ${preset.name} »`,
      body:
        `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${count} champ(s) de la stratégie courante.\n\n` +
        `Mode : ${presetMode() === "replace" ? "remplacement (les champs absents du preset sont supprimés)" : "fusion (les champs absents du preset sont conservés)"}.\n` +
        (preset.description ? `\n${preset.description}` : ""),
      level: presetMode() === "replace" ? "danger" : "warn",
      entityName: preset.name,
      confirmLabel: `Appliquer (${verb})`,
    });
    if (!ok) return;
    setDraft(preview);
  }

  function resetToInitial() {
    setDraft({ ...props.initialPolicy });
    setShowJson(false);
    setJsonError(null);
  }

  function commitJson() {
    try {
      const parsed = JSON.parse(jsonText());
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("La policy doit être un objet JSON.");
      }
      setDraft({ ...draft(), ...parsed });
      setShowJson(false);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div class="policy-editor">
      <div class="policy-editor-toolbar">
        <span class="policy-editor-title">Stratégie</span>
        <span class="policy-editor-scope-badge">
          {props.scopeType} · {props.scopeLabel || props.scopeId}
        </span>
        <Show when={isDirty()}>
          <span class="policy-editor-dirty-dot" title="Modifications non sauvegardées" />
        </Show>

        <div class="spacer" />

        {/* Preset selector */}
        <Show when={(props.presets || []).length > 0}>
          <select
            class="policy-editor-select"
            value=""
            onChange={(e) => {
              const id = e.currentTarget.value;
              if (id) void applyPresetById(id);
              e.currentTarget.value = "";
            }}
            title="Appliquer un preset"
          >
            <option value="">+ Preset…</option>
            <For each={props.presets || []}>
              {(p) => (
                <option value={p.id}>
                  {p.global ? "★ " : "  "}
                  {p.name}
                </option>
              )}
            </For>
          </select>
          <select
            class="policy-editor-select"
            style={{ "min-width": "90px" }}
            value={presetMode()}
            onChange={(e) => setPresetMode(e.currentTarget.value as "merge" | "replace")}
            title="Mode d'application du preset"
          >
            <option value="merge">Fusionner</option>
            <option value="replace">Remplacer</option>
          </select>
        </Show>

        {/* Aperçu résolu (host seulement) */}
        <Show when={props.hostId}>
          <button class="btn btn--xs" onClick={() => setDrawerOpen(true)}>
            👁 Aperçu résolu
          </button>
        </Show>

        {/* JSON brut */}
        <button
          class="btn btn--xs"
          onClick={() => {
            if (!showJson()) setJsonText(JSON.stringify(draft(), null, 2));
            setShowJson((v) => !v);
          }}
        >
          {"{ }"} JSON
        </button>

        <Show when={isDirty()}>
          <button class="btn btn--xs" onClick={resetToInitial} disabled={busy()}>
            Annuler modif
          </button>
        </Show>
        <button class="btn btn--xs btn--primary" onClick={save} disabled={!isDirty() || busy()}>
          {busy() ? "…" : "Sauver"}
        </button>
      </div>

      <Show when={err()}>
        <div class="policy-editor-error">{err()}</div>
      </Show>

      {/* Liste des champs groupés */}
      <For each={groupedFields()}>
        {(g) => {
          const collapsed = () => collapsedGroups().has(g.group.id);
          return (
            <div class="policy-editor-group">
              <div class="policy-editor-group-header" onClick={() => toggleGroup(g.group.id)}>
                <span class="policy-editor-group-title">
                  {g.group.label}
                  <Show when={g.fields.some((f) => dirtyKeys().has(f.key))}>
                    <span class="policy-editor-dirty-dot" style={{ "margin-left": "6px" }} />
                  </Show>
                </span>
                <span class={`policy-editor-group-chev ${collapsed() ? "" : "open"}`}>▶</span>
              </div>
              <Show when={!collapsed()}>
                <div class="policy-editor-group-body">
                  <For each={g.fields}>
                    {(field) => {
                      const value = () => draft()[field.key];
                      const dirty = () => dirtyKeys().has(field.key);
                      const showField = () => !field.sensitive || showSensitive() || value() != null;
                      return (
                        <Show when={showField()}>
                          <div class="policy-editor-field">
                            <div class="policy-editor-field-label">
                              <span class="key">{field.label}</span>
                              <Show when={field.help}>
                                <span class="help">{field.help}</span>
                              </Show>
                              <span class="code">{field.key}</span>
                            </div>
                            <div class="policy-editor-field-value">
                              <Show when={dirty()}>
                                <button
                                  class="btn btn--xs"
                                  title="Réinitialiser ce champ"
                                  onClick={() => clearField(field.key)}
                                  style={{ opacity: 0.7 }}
                                >
                                  ↺
                                </button>
                              </Show>
                              <FieldInput field={field} value={value()} onChange={(v) => setField(field.key, v)} />
                            </div>
                          </div>
                        </Show>
                      );
                    }}
                  </For>
                </div>
              </Show>
            </div>
          );
        }}
      </For>

      {/* Champs sensibles masqués : toggle global */}
      <Show when={POLICY_FIELDS.some((f) => f.sensitive)}>
        <label style={{ display: "inline-flex", "align-items": "center", gap: "6px", "font-size": "11px" }}>
          <input
            type="checkbox"
            checked={showSensitive()}
            onChange={(e) => setShowSensitive(e.currentTarget.checked)}
          />
          Afficher les champs sensibles (tokens)
        </label>
      </Show>

      {/* Champs hors schéma (legacy/inconnus) */}
      <Show when={unknownKeys().length > 0}>
        <div class="policy-editor-group">
          <div class="policy-editor-group-header">
            <span class="policy-editor-group-title" style={{ color: "var(--text-3)" }}>
              Champs hors schéma ({unknownKeys().length})
            </span>
          </div>
          <div class="policy-editor-group-body">
            <For each={unknownKeys()}>
              {(key) => (
                <div class="policy-editor-field">
                  <div class="policy-editor-field-label">
                    <span class="key" style={{ color: "var(--text-3)" }}>
                      {key}
                    </span>
                    <span class="code">non documenté / legacy</span>
                  </div>
                  <div class="policy-editor-field-value">
                    <input
                      class="policy-editor-input"
                      value={typeof draft()[key] === "string" ? String(draft()[key]) : JSON.stringify(draft()[key])}
                      onInput={(e) => setField(key, e.currentTarget.value)}
                    />
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* JSON brut */}
      <Show when={showJson()}>
        <div class="policy-editor-group">
          <div class="policy-editor-group-header" onClick={() => setShowJson(false)}>
            <span class="policy-editor-group-title">JSON brut</span>
            <span class="policy-editor-group-chev">▶</span>
          </div>
          <div class="policy-editor-group-body">
            <div class="policy-editor-json-warn">
              ⚠ Édition brute : aucune validation. Utiliser pour les champs avancés ou hors schéma.
            </div>
            <textarea
              class="policy-editor-json"
              value={jsonText()}
              onInput={(e) => setJsonText(e.currentTarget.value)}
            />
            <Show when={jsonError()}>
              <div class="policy-editor-error">{jsonError()}</div>
            </Show>
            <div style={{ display: "flex", gap: "6px", "margin-top": "6px" }}>
              <button class="btn btn--xs btn--primary" onClick={commitJson}>
                Appliquer le JSON
              </button>
              <button class="btn btn--xs" onClick={() => setShowJson(false)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={props.onCancel}>
        <div style={{ display: "flex", "justify-content": "flex-end", gap: "6px", "margin-top": "8px" }}>
          <button class="btn btn--xs" onClick={() => props.onCancel?.()}>
            Fermer
          </button>
          <button class="btn btn--xs btn--primary" onClick={save} disabled={!isDirty() || busy()}>
            {busy() ? "…" : "Sauver la stratégie"}
          </button>
        </div>
      </Show>

      {/* Drawer aperçu résolu (host only) */}
      <Show when={drawerOpen() && props.hostId}>
        <ResolvedPolicyDrawer
          hostId={props.hostId!}
          hostLabel={props.scopeLabel}
          onClose={() => setDrawerOpen(false)}
        />
      </Show>
    </div>
  );
};

/** Input typé selon le type de champ. Un seul return JSX (règle Solid). */
function FieldInput(props: {
  field: PolicyFieldDef;
  value: unknown;
  onChange: (v: PolicyObject[string]) => void;
}): JSX.Element {
  const field = props.field;

  return (
    <Show
      when={field.type === "bool"}
      fallback={
        <Show
          when={field.type === "enum"}
          fallback={
            <Show
              when={field.type === "number"}
              fallback={
                // string (default)
                <input
                  class="policy-editor-input"
                  type={field.sensitive ? "password" : "text"}
                  value={(props.value as string) ?? ""}
                  placeholder={field.sensitive ? "(non défini)" : ""}
                  autocomplete="off"
                  onInput={(e) => props.onChange(e.currentTarget.value)}
                />
              }
            >
              <NumberFieldInput field={field} value={props.value} onChange={props.onChange} />
            </Show>
          }
        >
          <select
            class="policy-editor-select"
            value={(props.value as string) ?? ""}
            onChange={(e) => props.onChange(e.currentTarget.value)}
          >
            <option value="">— (défaut) —</option>
            <For each={field.options || []}>{(opt) => <option value={opt}>{opt}</option>}</For>
          </select>
        </Show>
      }
    >
      <label class="policy-editor-toggle">
        <input
          type="checkbox"
          checked={Boolean(props.value)}
          onChange={(e) => props.onChange(e.currentTarget.checked)}
        />
        <span class="policy-editor-toggle-slider" />
      </label>
    </Show>
  );
}

/** Champ number : extrait dans un composant pour garder props.value réactif. */
function NumberFieldInput(props: {
  field: PolicyFieldDef;
  value: unknown;
  onChange: (v: PolicyObject[string]) => void;
}): JSX.Element {
  const n = () => Number(props.value);
  const invalid = () => !Number.isFinite(n());
  return (
    <input
      type="number"
      class={`policy-editor-input ${invalid() ? "invalid" : ""}`}
      value={Number.isFinite(n()) ? String(n()) : ""}
      min={props.field.min}
      max={props.field.max}
      onInput={(e) => {
        const parsed = Number(e.currentTarget.value);
        props.onChange(Number.isFinite(parsed) ? parsed : undefined);
      }}
    />
  );
}

export default PolicyEditor;
